import { formatFormSubmissionCreatedAtForDisplay } from './form-submission-date.util';
import { formatSubmissionDataForCsv } from './form-submission-cell-format.util';
import { collectSortedDataKeysFromSubmissions } from './form-submission-schema.util';

const trimString = (value) => String(value ?? '').trim();

const getSubmissionDataObject = (submission) => {
  const data = submission?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }
  return data;
};

const getUserDisplayName = (person) => {
  const username = trimString(person?.username);
  if (username !== '') {
    return username;
  }
  const name = `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim();
  if (name !== '') {
    return name;
  }
  return trimString(person?.docId);
};

/**
 * Stable id for a submission row inside export archives (prefers API `id`, else `row{index}`).
 * @param {{ id?: string | number } | null | undefined} submission
 * @param {number} submissionIndex
 * @returns {string}
 */
export const getSubmissionExportKey = (submission, submissionIndex) => {
  const id = submission?.id;
  if (id != null && String(id).trim() !== '') {
    return String(id).trim();
  }
  return `row${submissionIndex}`;
};

const getSubmittedAtValue = (submission) => formatFormSubmissionCreatedAtForDisplay(submission?.created_at);

/**
 * @typedef {object} SubmissionCsvCellContext
 * @property {readonly string[]} dataKeys
 * @property {ReadonlySet<string>} signatureKeys
 * @property {ReadonlyMap<string, string>} currencyMeta
 * @property {ReadonlySet<string>} selectboxKeys
 * @property {ReadonlySet<string>} surveyKeys
 * @property {ReadonlySet<string>} datetimeKeys
 * @property {ReadonlyMap<string, string>} [signatureZipPathMap] — keys `${submissionExportKey}::${fieldKey}` → relative zip path
 */

/**
 * @param {object} submission
 * @param {number} submissionIndex
 * @param {SubmissionCsvCellContext} ctx
 */
const buildDataCellsForSubmission = (submission, submissionIndex, ctx) => {
  const {
    dataKeys,
    signatureKeys,
    currencyMeta,
    selectboxKeys,
    surveyKeys,
    datetimeKeys,
    signatureZipPathMap,
  } = ctx;
  const data = getSubmissionDataObject(submission);
  const subKey = getSubmissionExportKey(submission, submissionIndex);
  return dataKeys.map((key) => {
    const cell = data[key];
    if (signatureKeys.has(key) && signatureZipPathMap) {
      const zipPath = signatureZipPathMap.get(`${subKey}::${key}`);
      if (zipPath !== undefined) {
        return zipPath;
      }
    }
    return formatSubmissionDataForCsv(cell, {
      isSignatureField: signatureKeys.has(key),
      currencyCode: currencyMeta.get(key),
      isSelectboxField: selectboxKeys.has(key),
      isSurveyField: surveyKeys.has(key),
      isDatetimeField: datetimeKeys.has(key),
    });
  });
};

/**
 * @param {object} submission
 * @param {number} submissionIndex
 * @param {SubmissionCsvCellContext} ctx
 */
const buildSubmissionCsvRow = (submission, submissionIndex, ctx) => {
  const user = getUserDisplayName(submission?.person);
  const locationName = trimString(submission?.location_name);
  const submittedAt = getSubmittedAtValue(submission);
  const cells = buildDataCellsForSubmission(submission, submissionIndex, ctx);
  return [user, locationName, submittedAt, ...cells];
};

const buildSubmissionsCsvHeaders = (dataKeys) => ['User', 'Location', 'Submitted', ...dataKeys];

const buildSubmissionsCsvFilename = (formTitle) => {
  const safeTitle = trimString(formTitle).replaceAll(/\s+/g, '-');
  return safeTitle ? `form-submissions-${safeTitle}.csv` : 'form-submissions.csv';
};

/**
 * Assemble the CSV export payload (`filename`, `headers`, `rows`) for form submissions.
 * @param {{
 *   submissions: readonly object[],
 *   signatureKeys: ReadonlySet<string>,
 *   currencyMeta?: ReadonlyMap<string, string>,
 *   selectboxKeys?: ReadonlySet<string>,
 *   surveyKeys?: ReadonlySet<string>,
 *   datetimeKeys?: ReadonlySet<string>,
 *   formTitle: string,
 *   signatureZipPathMap?: ReadonlyMap<string, string>,
 * }} input
 */
export const buildSubmissionsCsvExportPayload = ({
  submissions,
  signatureKeys,
  currencyMeta = new Map(),
  selectboxKeys = new Set(),
  surveyKeys = new Set(),
  datetimeKeys = new Set(),
  formTitle,
  signatureZipPathMap,
}) => {
  const dataKeys = collectSortedDataKeysFromSubmissions(submissions);
  const headers = buildSubmissionsCsvHeaders(dataKeys);
  const cellCtx = {
    dataKeys,
    signatureKeys,
    currencyMeta,
    selectboxKeys,
    surveyKeys,
    datetimeKeys,
    signatureZipPathMap,
  };
  const rows = submissions.map((submission, submissionIndex) =>
    buildSubmissionCsvRow(submission, submissionIndex, cellCtx),
  );
  return {
    filename: buildSubmissionsCsvFilename(formTitle),
    headers,
    rows,
  };
};
